import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarClock,
  CheckSquare,
  Clock,
  Radio,
  RefreshCw,
  Sparkles,
  Users,
} from 'lucide-react'
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  IconButton,
  LinkButton,
  SkeletonList,
  Tooltip,
} from '@/components/ui'
import { PageHeader, PlatformIcon, QueryBoundary, StatTile } from '@/components/domain'
import { UsageChart } from '@/components/charts'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatDayLabel, formatDuration, formatNumber, formatRelative, formatTime } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import {
  useMeetings,
  useRefreshUpcomingMeetings,
  useUpcomingMeetings,
} from '@/features/meetings/hooks'
import { useActionItems } from '@/features/actions/hooks'
import { MeetingListItem } from '@/features/meetings/components/MeetingListItem'
import { ScreenCaptureControl } from '@/features/meetings/components/ScreenCaptureControl'
import type { AnalyticsOverview, UpcomingMeetings } from '@/types'

/**
 * The landing screen. It answers three questions in order, top to bottom:
 * what is happening right now, what did I miss, and what do I owe.
 *
 * Anything live (a meeting being recorded) goes above the fold and unmissable —
 * it is also the one place a user can catch a recording they did not expect,
 * which matters when the invite is the only consent artefact.
 */
export function DashboardPage() {
  const t = useT()
  const user = useSessionStore((s) => s.user)
  const recent = useMeetings({ pageSize: 6, page: 1 })
  const upcoming = useUpcomingMeetings()
  const refreshUpcoming = useRefreshUpcomingMeetings()
  const myActions = useActionItems({ status: 'open', owner: 'me' })
  const analytics = useQuery({
    queryKey: qk.analytics.overview('30d'),
    queryFn: () => api.get<AnalyticsOverview>('/analytics/overview'),
  })

  const live = recent.data?.items.filter((m) => m.status === 'recording' || m.status === 'joining') ?? []
  const firstName = user?.name.split(' ')[0] ?? t('dashboard.greetingFallback')

  return (
    <div>
      <PageHeader

        title={t('dashboard.greeting', { name: firstName })}
        description={t('dashboard.subtitle')}

      />

      {live.length > 0 && (
        <Card className="mb-4 border-danger/30 bg-danger-subtle/40">
          <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Badge tone="danger" dot variant="solid">
              {t('dashboard.recordingNow')}
            </Badge>
            <ul className="min-w-0 flex-1 space-y-1">
              {live.map((meeting) => (
                <li key={meeting.id} className="flex items-center gap-2 text-base text-fg">
                  <PlatformIcon platform={meeting.platform} size="sm" />
                  <Link to={`/meetings/${meeting.id}`} className="truncate font-medium hover:underline">
                    {meeting.title}
                  </Link>
                  <span className="text-sm text-fg-muted">{t('dashboard.startedAt', { time: formatTime(meeting.startsAt) })}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label={t('dashboard.stat.meetingsRecorded')}
          value={formatNumber(analytics.data?.totalMeetings ?? 0)}
          delta={analytics.data?.trend?.totalMeetings ?? undefined}
          hint={t('dashboard.stat.last30')}
          icon={<CalendarClock />}
        />
        <StatTile
          label={t('dashboard.stat.hoursCaptured')}
          value={formatDuration((analytics.data?.totalMinutes ?? 0) * 60)}
          delta={analytics.data?.trend?.totalMinutes ?? undefined}
          hint={t('dashboard.stat.last30')}
          icon={<Clock />}
        />
        <StatTile
          label={t('dashboard.stat.actionItemsOpen')}
          value={formatNumber(
            (analytics.data?.actionItemsCreated ?? 0) - (analytics.data?.actionItemsCompleted ?? 0),
          )}
          delta={analytics.data?.trend?.actionItemsOpen ?? undefined}
          invertDelta
          hint={t('dashboard.stat.last30')}
          icon={<CheckSquare />}
        />
        <StatTile
          label={t('dashboard.stat.avgMeeting')}
          value={`${analytics.data?.avgMeetingMinutes ?? 0}m`}
          delta={analytics.data?.trend?.avgMeetingMinutes ?? undefined}
          invertDelta
          hint={t('dashboard.stat.last30')}
          icon={<Radio />}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title={t('dashboard.recentMeetings')}
            action={
              <LinkButton to="/meetings" size="sm" variant="ghost">
                {t('common.viewAll')}
              </LinkButton>
            }
          />
          <QueryBoundary
            isLoading={recent.isPending}
            error={recent.error}
            onRetry={recent.refetch}
            skeleton={<SkeletonList rows={5} className="p-4" />}
            isEmpty={recent.data?.items.length === 0}
            empty={
              <EmptyState
                icon={<Sparkles />}
                title={t('dashboard.noMeetingsTitle')}
                description={t('dashboard.noMeetingsBody')}
              />
            }
          >
            <ul>
              {recent.data?.items.map((meeting) => (
                <MeetingListItem key={meeting.id} meeting={meeting} />
              ))}
            </ul>
          </QueryBoundary>
        </Card>

        {/* On desktop Up next sits in flow at its natural height and the
            action-items card fills whatever is left of the row. That card's
            content is absolute inside its wrapper, so the wrapper only asks
            for its 8rem floor: the row is as tall as the Recent meetings card,
            or as Up next plus that floor when Recent is shorter (a new
            workspace, or a disconnected calendar), and the list scrolls inside
            the card instead of spilling past the column. Below lg it is an
            ordinary stack. The min height is a floor for a short Recent
            meetings card. */}
        <div className="lg:flex lg:min-h-[25rem] lg:flex-col">
          <div className="flex flex-col gap-4 lg:flex-1">
          <UpNextCard
            data={upcoming.data}
            isLoading={upcoming.isLoading}
            isRefreshing={refreshUpcoming.isPending}
            onRefresh={() => refreshUpcoming.mutate()}
          />

          <div className="lg:relative lg:min-h-[8rem] lg:flex-1">
          <Card className="flex flex-col lg:absolute lg:inset-0">
            <CardHeader
              title={t('dashboard.yourActionItems')}
              action={
                <div className="flex items-center gap-2">
                  <Badge tone="brand">{myActions.data?.items.length ?? 0}</Badge>
                  <LinkButton to="/actions" size="sm" variant="ghost">
                    {t('dashboard.seeAll')}
                  </LinkButton>
                </div>
              }
            />
            <CardBody className="min-h-0 flex-1 overflow-y-auto p-0">
              {myActions.data?.items.length ? (
                <ul className="divide-y divide-border">
                  {myActions.data.items.slice(0, 4).map((item) => (
                    <li key={item.id} className="px-4 py-2.5">
                      <Link
                        to={`/meetings/${encodeURIComponent(item.meetingId)}?tab=actions&actionItemId=${encodeURIComponent(item.id)}`}
                        title={item.text}
                        className="block truncate text-base text-fg hover:underline"
                      >
                        {item.text}
                      </Link>
                      <span className="mt-0.5 block truncate text-sm text-fg-subtle">{item.meetingTitle}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState size="sm" title={t('dashboard.nothingAssigned')} />
              )}
            </CardBody>
          </Card>
          </div>
          </div>
        </div>
      </div>

      <Card className="mt-4">
        <CardHeader title={t('dashboard.recordingVolume')} description={t('dashboard.recordingVolumeDesc')} />
        <CardBody>
          {analytics.data ? <UsageChart data={analytics.data.usage} /> : <SkeletonList rows={3} />}
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * "Up next" — the meetings the recorder is going to walk into.
 *
 * IT USED TO SAY "Nothing scheduled" TO PEOPLE WITH A FULL AFTERNOON, and the
 * reason was upstream of this component: it read only `sabha.meetings` rows with
 * `status = 'scheduled'`, and the scheduler writes that row five minutes before
 * a meeting starts, then moves it to `recording` seconds later. The card was
 * accurate about a five-minute sliver and empty the rest of the time. The list
 * now comes mostly from the bot mailbox's calendar (`domain.upcoming`).
 *
 * Two things are therefore rendered that a naive list would not bother with.
 * A calendar entry is NOT LINKED: there is no meeting row behind its id yet, so
 * a link would 404. And an empty list distinguishes "your diary is clear" from
 * "we cannot see your diary" — collapsing those two is the shape of the original
 * bug, and it would hide a broken Graph credential indefinitely.
 */
function UpNextCard({
  data,
  isLoading,
  isRefreshing,
  onRefresh,
}: {
  data: UpcomingMeetings | undefined
  isLoading: boolean
  isRefreshing: boolean
  onRefresh: () => void
}) {
  const t = useT()
  const items = data?.items ?? []
  const calendar = data?.calendar
  const disconnected = calendar && !calendar.configured

  return (
    <Card className="flex shrink-0 flex-col">
      <CardHeader
        title={t('dashboard.upNext')}
        description={t('dashboard.upNextDesc')}
        action={
          <Tooltip
            content={
              calendar?.refreshedAt
                ? t('dashboard.checkedRelative', { when: formatRelative(calendar.refreshedAt) })
                : t('dashboard.checkForInvites')
            }
          >
            <IconButton
              size="sm"
              label={t('dashboard.refreshUpcoming')}
              onClick={onRefresh}
              disabled={isRefreshing}
              icon={<RefreshCw className={isRefreshing ? 'animate-spin' : undefined} />}
            />
          </Tooltip>
        }
      />
      {/* Three rows' worth (rows render ~4.05rem each), then it scrolls. The
          cap applies to the list only, so the calendar-status message below is
          never cut off behind a scrollbar. */}
      <CardBody className={`min-h-0 overflow-y-auto p-0${items.length ? ' max-h-[12.125rem]' : ''}`}>
        {isLoading ? (
          <div className="px-4 py-3">
            <SkeletonList rows={3} />
          </div>
        ) : items.length ? (
          <ul className="divide-y divide-border">
            {items.map((meeting) => (
              <li key={meeting.id} className="flex items-start gap-2.5 px-4 py-2.5">
                <span className="mt-0.5">
                  <PlatformIcon platform={meeting.platform} size="sm" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 truncate text-base text-fg">{meeting.title}</span>
                    {/* The recorder is already committed to this one. Worth
                        saying, because it is the only status a person can
                        still act on by cancelling the meeting. A cancelled
                        meeting is never returned by the API, and a
                        rescheduled one is just this meeting at its new time. */}
                    {meeting.source === 'database' && (
                      <Badge tone="brand">
                        {meeting.status === 'joining' ? t('dashboard.badge.joining') : t('dashboard.badge.claimed')}
                      </Badge>
                    )}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-sm text-fg-subtle">
                    <span>
                      {formatDayLabel(meeting.startsAt)} · {formatTime(meeting.startsAt)}
                    </span>
                    {meeting.attendeeCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Users aria-hidden className="h-3.5 w-3.5" />
                        {meeting.attendeeCount}
                      </span>
                    )}
                  </span>
                </span>
                {/* On the ROW, not on a meeting page: the scheduler writes a
                    meeting's database row at claim time, five minutes before it
                    starts, so by the time there is a page to put this on the
                    decision has already been made. */}
                <span className="mt-0.5 shrink-0">
                  <ScreenCaptureControl meetingId={meeting.id} title={meeting.title} />
                </span>
              </li>
            ))}
          </ul>
        ) : disconnected ? (
          <EmptyState
            size="sm"
            icon={<CalendarClock />}
            title={t('dashboard.calendarNotConnected')}
            description={t('dashboard.calendarNotConnectedBody')}
          />
        ) : calendar && !calendar.ok ? (
          <EmptyState
            size="sm"
            icon={<CalendarClock />}
            title={t('dashboard.calendarUnreachable')}
            description={t('dashboard.calendarUnreachableBody')}
          />
        ) : (
          <EmptyState size="sm" title={t('dashboard.nothingScheduled')} description={t('dashboard.calendarClear')} />
        )}
      </CardBody>
      {/* A stale list is worse than a missing one: it looks authoritative. */}
      {calendar?.stale && items.length > 0 && (
        <div className="border-t border-border px-4 py-2 text-sm text-warning">
          {t('dashboard.staleCalendar')}
        </div>
      )}
    </Card>
  )
}
